import {
  Box,
  Button,
  Container,
  Flex,
  Group,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useFocusTrap } from "@mantine/hooks";
import Link from "next/link";
import { useRouter } from "next/router";
import type { FC } from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { MdCancel, MdClose, MdSearch } from "react-icons/md";
import { AvatarIconMemo } from "src/component/element";
import { useCommentCreateFumufumuFormContext } from "src/component/pageComponent/Fumufumu/CommentForm/hooks";
import { useFumufumuFormContext } from "src/component/pageComponent/Fumufumu/CreateForm/hooks";
import { useEditFumufumuFormContext } from "src/component/pageComponent/Fumufumu/EditFrom/hooks";
import { useSearchReset } from "src/hooks";
import { DrawerMenuMemo } from "src/layout/AppLayout/DrawerMenu";
import {
  CustomModal,
  errorShowNotification,
  successShowNotification,
} from "src/lib/mantine";
import { addSearch, selectSearch } from "src/reducks/SearchStore";
import { useDeleteFumufumuMutation } from "src/service/FumuFumuApi";
import { useGetUserSettingsQuery } from "src/service/UsersApi";
import { useAppDispatch, useAppSelector } from "src/store/hooks";

import styles from "./FumufumuHeader.module.scss";
import {
  handleChange,
  handleCompositionEnd,
  toggleSearch,
  useFumufumuPageTitle,
} from "./hooks";

export const FumufumuHeader: FC = () => {
  const { data: userData } = useGetUserSettingsQuery();
  useSearchReset();
  const [draftOpened, setDraftOpened] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [isDisabledDraftModal, setIsDisabledDraftModal] =
    useState<boolean>(false);
  const router = useRouter();
  const { checkedPostPage, pagePath, pageTitle } = useFumufumuPageTitle();
  const focusTrapRef = useFocusTrap();
  const dispatch = useAppDispatch();
  const searchState = useAppSelector(selectSearch);

  const fumufumuCreateForm = useFumufumuFormContext();
  const fumufumuEditForm = useEditFumufumuFormContext();
  const fumufumuCreateCommentForm = useCommentCreateFumufumuFormContext();
  const [deleteFumufumu] = useDeleteFumufumuMutation();

  // 各投稿画面でフムフムが入力されていないかを判断
  useEffect(() => {
    if (pagePath === "post") {
      setIsDisabledDraftModal(fumufumuCreateForm.values.text === "");
    }
    if (pagePath === "edit") {
      setIsDisabledDraftModal(fumufumuEditForm.values.text === "");
    }
    if (pagePath === "comment") {
      setIsDisabledDraftModal(fumufumuCreateCommentForm.values.text === "");
    }
  }, [
    fumufumuCreateCommentForm.values.text,
    fumufumuCreateForm.values.text,
    fumufumuEditForm.values.text,
    pagePath,
  ]);

  // フムフムが入力されていないときにページに戻る戻す
  const editCancel = useCallback(() => {
    if (pagePath === "post") {
      router.push("/fumufumu/public");
    }
    if (pagePath === "edit") {
      router.push("/fumufumu/draft");
    }
    if (pagePath === "comment") {
      if (history.length === 0) {
        router.push("/fumufumu/public");
      } else {
        history.back();
      }
    }
  }, [pagePath, router]);

  // 各下書き投稿用のモーダルを閉じる
  const closeModal = () => {
    if (pagePath === "post") {
      fumufumuCreateForm.setValues({ isDraft: false });
    }
    if (pagePath === "edit") {
      fumufumuEditForm.setValues({
        isDraft: false,
      });
    }
    return setDraftOpened(false);
  };

  // 各下書き投稿用のモーダルを開く
  const openModal = useCallback(() => {
    if (pagePath === "post") {
      fumufumuCreateForm.setValues({ isDraft: true });
    }
    if (pagePath === "edit") {
      fumufumuEditForm.setValues({
        isDraft: true,
      });
    }

    return setDraftOpened(true);
  }, [fumufumuCreateForm, fumufumuEditForm, pagePath]);

  // ドロワーの開閉
  const toggleDrawer: () => void = () => {
    setDrawerOpen((prevState) => {
      return !prevState;
    });
  };

  return (
    <>
      <CustomModal
        setOpened={setDraftOpened}
        opened={draftOpened}
        title="投稿を編集中です"
        onCloseAction={closeModal}
      >
        {pagePath === "comment" ? (
          <>
            <Text className={styles.modalBody}>
              戻るか、削除するかを選択してください。
            </Text>
            <Group position="center" className={styles.modalButtonWrap}>
              <Button
                onClick={() => {
                  setDraftOpened(false);
                }}
                className={styles.commentBackButton}
              >
                戻る
              </Button>
              <Button
                className={styles.commentDeleteButton}
                onClick={() => {
                  setDraftOpened(false);
                  fumufumuCreateCommentForm.reset();
                  return router.push("/fumufumu/public");
                }}
              >
                削除
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Text className={styles.modalBody}>
              編集中のフムフムが消えてしまいますが、大丈夫ですか？
            </Text>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                closeModal();
              }}
            >
              <Group position="center" className={styles.modalButtonWrap}>
                <Button
                  className={styles.modalBackButton}
                  onClick={async () => {
                    if (pagePath === "edit") {
                      await deleteFumufumu(fumufumuEditForm.values.id)
                        .unwrap()
                        .then(() => {
                          successShowNotification("投稿の削除に成功しました。");
                        })
                        .catch((e) => {
                          if (e.data) {
                            errorShowNotification(e.data.error?.message);
                          } else {
                            errorShowNotification("投稿に失敗しました。");
                          }
                        });
                    } else {
                      fumufumuCreateForm.reset();
                    }
                    setDraftOpened(false);
                    return router.push("/fumufumu/public");
                  }}
                >
                  削除
                </Button>
                <Button type="submit" className={styles.modalSubmitButton}>
                  戻る
                </Button>
              </Group>
            </form>
          </>
        )}
      </CustomModal>
      <Box component="header" className={styles.header}>
        <Container px={24}>
          <Flex py={16} justify="space-between" align="center">
            <Group>
              <Link
                href="/mypage"
                className={styles.headerLink}
                passHref
                aria-label="マイページへ遷移"
              >
                <AvatarIconMemo
                  avatar={userData?.profileImage}
                  background={userData?.profileBgImage}
                />
              </Link>
            </Group>
            <Group spacing={5} position="center">
              {searchState.toggleSearch ? (
                <TextInput
                  ref={focusTrapRef}
                  placeholder="キーワードやタグを入力して検索"
                  onChange={(e) => {
                    dispatch(addSearch(e.currentTarget.value));
                  }}
                  styles={() => {
                    return {
                      input: {
                        border: "none",
                        fontSize: "1.6rem",
                        height: "20px",
                        minHeight: "20px",
                      },
                    };
                  }}
                />
              ) : (
                <h1>{pageTitle}</h1>
              )}
            </Group>
            <Group spacing={5}>
              {checkedPostPage ? (
                <UnstyledButton
                  onClick={isDisabledDraftModal ? editCancel : openModal}
                  aria-label="下書き保存用のモーダルを開く"
                  // disabled={isDisabledDraftModal}
                >
                  <MdClose size={24} color="#2D3336" />
                </UnstyledButton>
              ) : (
                <UnstyledButton onClick={toggleSearch} aria-label="検索">
                  {searchState.toggleSearch ? (
                    <MdCancel size={24} />
                  ) : (
                    <MdSearch size={24} />
                  )}
                </UnstyledButton>
              )}
            </Group>
          </Flex>
        </Container>
      </Box>
      {/* pc header */}
      <header className={styles.pcHeader}>
        <div className={styles.pcHeaderInner}>
          <Flex align="center" justify="flex-end">
            <Group spacing={5} position="center" mr={24}>
              {searchState.toggleSearch && (
                <TextInput
                  ref={focusTrapRef}
                  onChange={handleChange}
                  onCompositionEnd={handleCompositionEnd}
                  placeholder="キーワードやタグを入力して検索"
                  styles={{ wrapper: { minWidth: "500px" } }}
                />
              )}
            </Group>
            <Group spacing={5}>
              {checkedPostPage ? (
                <UnstyledButton
                  onClick={isDisabledDraftModal ? editCancel : openModal}
                  aria-label="下書き保存用のモーダルを開く"
                >
                  <MdClose size={24} color="#2D3336" />
                </UnstyledButton>
              ) : (
                <UnstyledButton onClick={toggleSearch} aria-label="検索">
                  {searchState.toggleSearch ? (
                    <MdCancel size={24} />
                  ) : (
                    <MdSearch size={24} />
                  )}
                </UnstyledButton>
              )}
            </Group>
          </Flex>
        </div>
      </header>
      <DrawerMenuMemo
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        toggleDrawer={toggleDrawer}
      />
    </>
  );
};

export const FumufumuHeaderMemo = memo(FumufumuHeader);
